<template>
 <VDialog
    v-if="isAnotherPassengerDialogShow"
    :value="isAnotherPassengerDialogShow"
    width="425"
>
  <AnotherPassengerDialogDelete
    :newAnotherPassengersList="getNewPassengerList"
    :isAnotherPassengerDialogDeleteShow="isAnotherPassengerDialogDeleteShow"
    :anotherPassenger="anotherPassenger"
    @closeDeleteDialog="closeDeleteDialog"
    @populateUserProfileInfo="$emit('populateUserProfileInfo')"
  />
    <VCard
        :loading="isLoading"
        class="p-0 shadow-none border-radius-8 px-4 py-4 position-relative"
    >
    <VCardTitle class="pb-10 px-0 pt-0 d-flex justify-content-between align-center position-relative">
        <div>
          <span class="heading-6">
              {{ isEditMode ? $t('checkout.anotherPersonDetails.editPassenger'): $t('checkout.anotherPersonDetails.addNewPassenger') }}
          </span>
        </div>
    <VBtn
        icon
        class="grey-lighten-4 box-shadow-elevation-6"
        @click="closeDialog"
        >
        <VIcon color="textPrimary">mdi-close</VIcon>
        </VBtn>
    </VCardTitle>
    <VCardText class="p-0 pt-6">
        <div class="card-text-body">
        <ValidationObserver
            ref="newPassengerForm"
            name="newPassengerForm"
            class="breakpoint-xl"
        >
        <ValidationProvider
            ref="newPhone"
            v-slot="{ errors, ariaInput }"
            name="newPhone"
            :rules="phoneRules"
            :allowFalse="false"
            mode="eager"
        >
            <vue-tel-input-vuetify
                ref="phoneInputModal"
                v-bind="ariaInput"
                :value="typeof newPassenger.phone === 'string' ? newPassenger.phone : ''"
                v-phone-restrict
                :disabled="isCustomerPhoneUsed"
                class="body-1 pb-0"
                :loading="isLoadingCountries"
                :defaultCountry="defaultDialCode"
                disabledFetchingCountry
                :error="!!errors.length"
                :error-messages="$t(errors[0])"
                :placeholder="$t('authForm.enterPhoneNumber')"
                :mode="'international'"
                :label="$t('checkout.anotherPersonDetails.phone')"
                @input="onPhoneInput"
                @country-changed="(val) => selectCountry(val, false)"
            />
          </ValidationProvider>
          <VCheckbox
            v-model="isCustomerPhoneUsed"
            dense
            color="brandingPrimary"
            class="smaller-label body-2 mt-0 pb-4"
            @change="updatePhone"
          >
          <template #label>
            <span class="body-2">{{ $t('checkout.anotherPersonDetails.useMyPhoneNumber') }}</span>
          </template>
          </VCheckbox>
          <ValidationProvider
              ref="firstName"
              v-slot="{ errors, ariaInput }"
              name="firstName"
              :rules="{ required: true, max: 255, duplicate: { fullName: `${newPassenger.firstName} ${newPassenger.lastName}`, passengerList: getNewPassengerList }, atLeastTwoLetters: /[a-zA-Z]{2,}/, onlyLettersAndSymbols: firstSecondNameRegex }"
              :allowFalse="false"
              mode="eager"
          >
            <VTextField
                v-bind="ariaInput"
                class="py-0 mt-0"
                :hide-details="!errors.length"
                v-model="newPassenger.firstName"
                :error="!!errors.length"
                :error-messages="getErrorMessage(errors)"
                :label="$t('checkout.anotherPersonDetails.firstName')"
            />
        </ValidationProvider>
          <div class="pt-6">
            <ValidationProvider
              ref="lastName"
              v-slot="{ errors, ariaInput }"
              name="lastName"
              :rules="{ required: true, max: 255, duplicate: { fullName: `${newPassenger.firstName} ${newPassenger.lastName}`, passengerList: getNewPassengerList },  atLeastTwoLetters: /[a-zA-Z]{2,}/, onlyLettersAndSymbols: firstSecondNameRegex }"
              :allowFalse="false"
              mode="eager"
          >
            <VTextField
                v-bind="ariaInput"
                v-model="newPassenger.lastName"
                class="py-0 mt-0"
                :hide-details="!errors.length"
                :error="!!errors.length"
                :error-messages="validationErrorResolve(errors[0])"
                :label="$t('checkout.anotherPersonDetails.lastName')"
            />
          </ValidationProvider>
          </div>
        </ValidationObserver>
        <VBtn
          v-if="isEditMode"
          class="error--text header__link mt-6"
          color="error--text"
          :active="false"
          text
          @click="openDeleteDialog(true)"
          >
          <VIcon left>
            mdi-delete
          </VIcon>
            <span class="button error--text">
              {{ $t('checkout.anotherPersonDetails.deleteButton') }}
            </span>
          </VBtn>
        </div>
      </VCardText>
      <VCardActions class="px-0 pb-0 pt-6">
        <div class="d-flex align-center justify-content-between w-100">
          <div class="w-50 pr-2">
            <VBtn
              v-blur
              class="w-100 body-2 border-radius-8"
              outlined
              large
              color="textTertiary"
              @click="closeDialog"
            >
              {{ $t('general.cancel') }}
            </VBtn>
          </div>
          <div class="w-50 pl-2">
            <VBtn
              v-blur
              class="w-100 body-2 border-radius-8"
              dark
              large
              color="brandingPrimary"
              @click="updateAnotherPassengerList"
            >
              {{ $t('general.save') }}
            </VBtn>
          </div>
        </div>
      </VCardActions>
   </VCard>
</VDialog>
</template>

<script lang="ts">
export default {
  name: 'AnotherPassengerDialogForm',
};
</script>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import SUserService from '@slg/web-customer-shared/src/services/UserService';
import Constants from '@slg/web-customer-shared/src/utils/constants';
import { phoneFromTelInput } from '@slg/web-customer-shared/src/utils/telInput';
import AnotherPassengerDialogDelete from '@slg/web-customer-shared/src/components/checkout/passengerInfo/anotherPassengerDialogForm/AnotherPassengerDialogDelete.vue';
import { useGlobalErrorDialog, useValidationError } from '../../../../composables';

const props = defineProps({
  anotherPassengersList: {
    type: Array,
    default: () => [],
  },
  isAnotherPassengerDialogShow: {
    type: Boolean,
    default: false,
  },
  anotherPassenger: {
    type: Object,
    default: () => null,
  },
  isEditMode: {
    type: Boolean,
    default: false,
  },
  isItemSelected: {
    type: Boolean,
    default: false,
  },
  searchQuery: {
    type: String,
    default: '',
  },
  country: {
    type: String,
    default: '',
  },
  customerPhone: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['populateUserProfileInfo', 'updateLastEditedPassenger', 'closeDialog']);

const { setGlobalError } = useGlobalErrorDialog();
const { validationErrorResolve } = useValidationError();

const phoneInputModal = ref(null);
const newPassengerForm = ref(null);

const isLoading = ref(false);
const newPassenger = ref({
  passengerId: null,
  phone: null,
  firstName: '',
  lastName: '',
});
const isCustomerPhoneUsed = ref(false);
const isLoadingCountries = ref(false);
const phone = ref(null);
const firstSecondNameRegex = Constants.FIRST_SECOND_NAME_REGEX;
const isValidPhoneNumber = ref(true);
const phoneRules = computed(() => ({
  required: true,
  phoneNumber: {
    isValidPhoneNumber: isValidPhoneNumber.value,
  },
}));
const isAnotherPassengerDialogDeleteShow = ref(false);
const defaultDialCode = ref(null);

const getNewPassengerList = computed(() => (props.isEditMode
  ? props.anotherPassengersList.filter((passenger) => passenger.passengerId !== props.anotherPassenger.passengerId)
  : props.anotherPassengersList));

function getErrorMessage(errors) {
  if (!errors.length || errors[0] === 'validations.duplicateName') {
    return '';
  }
  return validationErrorResolve(errors[0]);
}

async function validatePhone() {
  nextTick(async () => {
    isValidPhoneNumber.value = phoneInputModal.value?.phoneObject.isValid;
  });
}

function onPhoneInput(value: unknown) {
  newPassenger.value.phone = phoneFromTelInput(value);
  validatePhone();
}

function findDefaultCountry() {
  nextTick(() => {
    const defaultCountry = phoneInputModal.value?.allCountries.find((country) => country.name.includes(props.country));
    defaultDialCode.value = defaultCountry?.iso2;
  });
}

async function selectCountry(val, isDefaultCountry = true) {
  nextTick(async () => {
    isLoadingCountries.value = true;
    if (isDefaultCountry) {
      const defaultCountry = phoneInputModal.value?.allCountries.find((country) => country.name.includes(props.country));
      defaultDialCode.value = defaultCountry?.iso2;
      newPassenger.value.phone = `+${defaultCountry?.dialCode}`;
    } else {
      newPassenger.value.phone = `+${val?.dialCode}`;
    }
    isLoadingCountries.value = false;
  });
}

function closeDialog() {
  newPassenger.value = {
    passengerId: null,
    phone: null,
    firstName: '',
    lastName: '',
  };
  isCustomerPhoneUsed.value = false;
  emit('closeDialog');
}

function closeDeleteDialog(isPassengerDeleted) {
  isAnotherPassengerDialogDeleteShow.value = false;
  if (isPassengerDeleted) {
    closeDialog();
  }
}

function openDeleteDialog() {
  isAnotherPassengerDialogDeleteShow.value = true;
}

function checkIsPassengerChanged() {
  return (JSON.stringify({...newPassenger.value, phone: phoneInputModal.value?.phoneObject?.number?.e164}) !== JSON.stringify(props.anotherPassenger)
    || phoneInputModal.value?.value?.replace(/\s/g, '') !== props.anotherPassenger.phone);
}

async function updateAnotherPassengerList() {
  newPassengerForm.value.validate().then(async (success) => {
    if (!success) {
      return;
    }
    nextTick(async () => {
      if (!await checkIsPassengerChanged()) {
        closeDialog();
        return;
      }

      isLoading.value = true;

      const newPassengerList = [...getNewPassengerList.value, { ...newPassenger.value, phone: phoneInputModal.value?.value?.replace(/\s/g, '') }];

      try {
        await SUserService.updateProfile({ anotherPassengerList: newPassengerList });
        emit('updateLastEditedPassenger', newPassenger.value.passengerId);
        emit('populateUserProfileInfo');
      } catch (e) {
        if (e.status !== 401) {
          await setGlobalError(e);
        }
      }

      isLoading.value = false;
      closeDialog();
    });
  });
}

async function updatePhone(isCustomerPhoneUsedValue) {
  if (isCustomerPhoneUsedValue) {
    newPassenger.value.phone = props.customerPhone;
    await validatePhone();
  } else {
    await selectCountry();
    await validatePhone();
  }
}

watch(() => props.isAnotherPassengerDialogShow, async () => {
  findDefaultCountry();
  if (props.isAnotherPassengerDialogShow && props.isEditMode) {
    newPassenger.value = { ...props.anotherPassenger };
    await validatePhone();
    newPassenger.value.phone = props.anotherPassenger.phone;
  }

  if (props.isAnotherPassengerDialogShow && !props.isEditMode && !props.isItemSelected) {
    await selectCountry();
    await selectCountry();
    if (Number.isNaN(Number(props.searchQuery))) {
      newPassenger.value.lastName = props.searchQuery;
    } else {
      setTimeout(() => {
        newPassenger.value.phone = `${newPassenger.value.phone}${props.searchQuery}`;
      }, 100);
    }
    newPassenger.value.passengerId = uuidv4();
  }

  if (props.isAnotherPassengerDialogShow && !props.isEditMode && props.isItemSelected) {
    setTimeout(async () => {
      await selectCountry();
    }, 100);
    newPassenger.value.passengerId = uuidv4();
  }
}, { deep: true });

watch(() => newPassenger.value.phone, async () => {
  if (isCustomerPhoneUsed.value) {
    newPassenger.value.phone = props.customerPhone;
    await validatePhone();
  }
  await validatePhone();
  isCustomerPhoneUsed.value = newPassenger.value.phone?.replace(/\s/g, '') === props.customerPhone;
}, { deep: true });
</script>
<style lang="scss" scoped>
.vue-tel-input-vuetify {
    height: 42px !important;
}

</style>
